import React from "react";
import { Link } from "react-router-dom";
import {
  CheckCircle2,
  Calendar,
  Clock,
  MapPin,
  ShieldCheck,
  ArrowLeft,
  Sparkles,
  ArrowRight,
  BookOpen,
} from "lucide-react";
import { Button } from "@/components/ui/button";

/**
 * ============================================================
 * WORKSHOP REGISTRATION SUCCESS COMPONENT
 * ============================================================
 * Polished confirmation screen shown after successful registration.
 * Explicitly DOES NOT expose private resume URLs or internal IDs.
 */
export default function WorkshopRegistrationSuccess({ workshop, studentName }) {
  if (!workshop) return null;

  return (
    <div className="w-full max-w-2xl mx-auto space-y-6 animate-in fade-in-50 zoom-in-95 duration-500">
      {/* Confirmation Glass Card */}
      <div className="rounded-3xl border border-white/15 bg-white/[0.04] backdrop-blur-2xl p-6 sm:p-10 shadow-[0_24px_70px_rgba(0,0,0,0.5)] space-y-8 relative overflow-hidden">
        {/* Glow Accent */}
        <div className="absolute top-0 right-0 w-72 h-72 bg-primary/10 rounded-full blur-3xl -z-10 pointer-events-none" />

        {/* Top Header & Celebration */}
        <div className="text-center space-y-3">
          <div className="w-16 h-16 rounded-2xl bg-green-500/15 border border-green-500/30 text-green-400 mx-auto flex items-center justify-center shadow-[0_0_30px_rgba(34,197,94,0.3)]">
            <CheckCircle2 className="w-9 h-9" />
          </div>

          <div className="space-y-1">
            <span className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold bg-primary/15 text-primary border border-primary/25">
              <Sparkles className="w-3.5 h-3.5" />
              <span>Cohort Registration Received</span>
            </span>

            <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white pt-2">
              You're Registered{studentName ? `, ${studentName}` : ""}!
            </h2>

            <p className="text-sm text-white/65 leading-relaxed max-w-md mx-auto">
              Your application for{" "}
              <strong className="text-white font-semibold">{workshop.title}</strong> has
              been submitted and logged into our student roster.
            </p>
          </div>
        </div>

        {/* Session Logistics Summary Card */}
        <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 space-y-4">
          <h3 className="text-xs uppercase tracking-wider text-white/50 font-semibold">
            Registered Session Summary
          </h3>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5 text-xs text-white/80">
            <div className="flex items-center gap-2.5 p-3 rounded-xl bg-white/[0.02] border border-white/5">
              <Calendar className="w-4 h-4 text-primary flex-shrink-0" />
              <div>
                <span className="text-[11px] text-white/45 block">Date</span>
                <span className="font-semibold text-white">{workshop.date}</span>
              </div>
            </div>

            <div className="flex items-center gap-2.5 p-3 rounded-xl bg-white/[0.02] border border-white/5">
              <Clock className="w-4 h-4 text-primary flex-shrink-0" />
              <div>
                <span className="text-[11px] text-white/45 block">Timing</span>
                <span className="font-semibold text-white">
                  {workshop.start_time} – {workshop.end_time}
                </span>
              </div>
            </div>

            <div className="flex items-center gap-2.5 p-3 rounded-xl bg-white/[0.02] border border-white/5">
              <MapPin className="w-4 h-4 text-primary flex-shrink-0" />
              <div>
                <span className="text-[11px] text-white/45 block">Format</span>
                <span className="font-semibold text-white">{workshop.mode}</span>
              </div>
            </div>

            <div className="flex items-center gap-2.5 p-3 rounded-xl bg-white/[0.02] border border-white/5">
              <ShieldCheck className="w-4 h-4 text-primary flex-shrink-0" />
              <div>
                <span className="text-[11px] text-white/45 block">Credential</span>
                <span className="font-semibold text-white">
                  {workshop.certificate_available
                    ? "Verified Certificate Included"
                    : "Attendance Credential"}
                </span>
              </div>
            </div>
          </div>
        </div>

        {/* What Happens Next Guidance */}
        <div className="rounded-2xl border border-primary/20 bg-primary/[0.04] p-5 space-y-2.5">
          <div className="flex items-center gap-2 text-xs font-bold text-primary tracking-wide uppercase">
            <BookOpen className="w-4 h-4" />
            <span>What Happens Next?</span>
          </div>
          <p className="text-xs sm:text-sm text-white/75 leading-relaxed">
            Our engineering faculty and admissions team will review your application and
            verified credentials. Selected cohort participants will be notified with access
            details, setup guides, and repository links prior to the session start.
          </p>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row items-center justify-center gap-3 pt-2">
          <Link to={`/workshops/${workshop.slug}`} className="w-full sm:w-auto">
            <Button
              variant="outline"
              className="w-full sm:w-auto rounded-xl border-white/15 bg-white/[0.04] text-xs font-semibold text-white hover:bg-white/10 hover:border-white/30 h-11 px-6"
            >
              <ArrowLeft className="w-3.5 h-3.5 mr-2" />
              <span>Workshop Details</span>
            </Button>
          </Link>

          <Link to="/workshops" className="w-full sm:w-auto">
            <Button className="w-full sm:w-auto rounded-xl bg-primary text-black hover:bg-primary/90 font-bold text-xs h-11 px-6 shadow-[0_0_20px_rgba(0,194,255,0.4)]">
              <span>Browse Catalog</span>
              <ArrowRight className="w-3.5 h-3.5 ml-2" />
            </Button>
          </Link>
        </div>
      </div>
    </div>
  );
}
